import React from 'react'
import { useLoaderData } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { TableCell, TableRow, Typography } from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'

import { formatBigMoney, formatDateYYYYMMDD, formatInteger } from '../../../packages/utils/numberFormatters'
import { DynamicIconButton } from '../../../utils/tooltip/DynamicIconButton'
import { FactsTable, FactsTableRowFlexibleColumns } from '../../components/FactsTable'
import { CompanyEntity } from '../../../types/CompanyEntity'
import { VwapTableRow } from './VwapTableRow'
import RoutedDropdownSelect from '../../components/RoutedDropdownSelect'
import { useQuery } from '../../../hooks/useQuery'

export const SHARE_SELECT_ROUTING_PARAM = 'selectedShare'

export const CompanyListedShares = () => {
    const company = useLoaderData()
    const { i18n, t } = useTranslation()
    const locale = i18n.language
    const query = useQuery()

    if (!company) return null

    const shares = company.shares
    const defaultItem = shares[0].id
    const selectedShare = shares.find((i) => i.id === query.get(SHARE_SELECT_ROUTING_PARAM)) || shares?.[0]

    if (!selectedShare) return null

    const tableRows = []
    const columns = [selectedShare]

    if (shares && shares.length > 0) {
        tableRows.push(
            <TableRow>
                <TableCell colSpan={2}>
                    {shares.length === 1 && <h4 style={{ margin: 0 }}>{selectedShare.instrumentName}</h4>}
                    {shares.length > 1 && (
                        <RoutedDropdownSelect
                            items={shares.map((s, index) => ({
                                id: s.id,
                                label: s.instrumentName || `Aktie ${index + 1}`,
                            }))}
                            isSlim
                            defaultItem={defaultItem}
                            routingParam={SHARE_SELECT_ROUTING_PARAM}
                        />
                    )}
                </TableCell>
            </TableRow>,
        )

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={'Lista'}
                columns={columns}
                getColumnValue={(share) => {
                    return share.marketplace
                }}
                lastColumnValue={null}
            />,
        )

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue={'ISIN'}
                columns={columns}
                getColumnValue={(share) => {
                    return share.isin
                }}
                lastColumnValue={null}
            />,
        )

        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue='Antal aktier'
                IconComponent={
                    <DynamicIconButton
                        title='Antal registrerade aktier per aktieslag. Definitionen kan variera för exempelvis dubbelnoterade aktier. Källa: Millistream'
                        IconComponent={InfoIcon}
                        placement='top'
                        padding='0px 4px'
                    />
                }
                columns={columns}
                getColumnValue={(share) => {
                    return formatInteger(share.millistreamShares, { locale })
                }}
                lastColumnValue={null}
            />,
        )
        tableRows.push(
            <FactsTableRowFlexibleColumns
                key={tableRows.length}
                firstColumnValue='Börsvärde'
                columns={columns}
                IconComponent={
                    <DynamicIconButton
                        title='Börsvärde baseras på totalt antal aktier plus maximal utspädning från hypotetiskt fulltecknade emissioner.'
                        IconComponent={InfoIcon}
                        placement='top'
                        padding='0px 4px'
                    />
                }
                getColumnValue={(share) => {
                    if (company.marketcap === null) {
                        return ''
                    }
                    return formatBigMoney(company.marketcap, {
                        locale,
                        currencySymbol: share.currency,
                    })
                }}
                lastColumnValue={null}
            />,
        )
        tableRows.push(<VwapTableRow key='vwap' sharesRange={columns} />)

        if (columns[0]?.delistedDate) {
            tableRows.push(
                <FactsTableRowFlexibleColumns
                    key={tableRows.length}
                    firstColumnValue={'Avnoterad'}
                    columns={columns}
                    getColumnValue={(share) => {
                        return (
                            <>
                                <Typography variant='subtitle2'>
                                    Aktien är avnoterad sedan {formatDateYYYYMMDD(share?.delistedDate)}
                                </Typography>
                                {share?.delistedReason ? (
                                    <Typography variant='subtitle2'>
                                        ({t(`site.instruments.${share.delistedReason}`)})
                                    </Typography>
                                ) : null}
                            </>
                        )
                    }}
                    lastColumnValue={null}
                />,
            )
        }
    }

    return tableRows.length > 0 ? <FactsTable rows={tableRows} /> : null
}
